/*
  Zoey's Birthday Sale bar.

  Applying goes through Shopify's own /discount/CODE route — the same route a
  Collabs personalised link uses — so the code lands in the session and in the
  readable `discount_code` cookie.

  Order of events, deliberately:

    1. tap Apply
    2. call /discount/CODE with fetch
    3. reload

  The reload is what restates the prices — creator-pricing.js reads the rate
  once on execute, so it only takes effect on a fresh load.

  The Lottie player and the discount badge are fetched only when the welcome
  dialog opens; nothing Lottie-related ships with the bar itself.
*/
(function () {
  var bar = document.querySelector('[data-zoey-bar]');
  if (!bar) return;

  var CODE = (bar.getAttribute('data-code') || '').trim();
  if (!CODE) return;

  var playerUrl = bar.getAttribute('data-player');
  var discountUrl = bar.getAttribute('data-discount-anim');
  var discountPercent = parseInt(bar.getAttribute('data-discount-percent'), 10);
  var discountAssets = null;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  var applyBtn = bar.querySelector('[data-zoey-apply]');
  var chip = bar.querySelector('[data-zoey-chip]');
  var copied = bar.querySelector('[data-zoey-copied]');

  /* ── analytics ──────────────────────────────────────────────────────────
     GA4's own promotion events, so this lands in Reports → Monetization →
     Promotions rather than needing a custom exploration.

     No `send_to`: gtag already knows every configured destination, and naming
     a measurement ID here would rot the moment the Google & YouTube channel is
     reconnected — which has happened before and silently changed the tag IDs.
     The Ads and Merchant Center tags ignore an event with no conversion label.

     Tracking is strictly optional. Every call is guarded, and nothing here is
     allowed to delay or block the discount. */
  function sendEvent(name, params, whenSent) {
    var done = typeof whenSent === 'function' ? whenSent : function () {};

    if (typeof window.gtag !== 'function') return done();

    var called = false;
    function once() {
      if (called) return;
      called = true;
      done();
    }

    var payload = {};
    for (var key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) payload[key] = params[key];
    }
    payload.event_callback = once;

    try {
      window.gtag('event', name, payload);
    } catch (e) {
      return once();
    }

    // India's gtag batches anything that isn't the first event of an idle
    // period on a ~5s window. Apply reloads immediately, so without waiting for
    // the callback the hit dies in that queue — and it would die precisely for
    // the quickest, most motivated shoppers. Cap the wait so a blocked or slow
    // gtag can never hold someone up.
    setTimeout(once, 700);
  }

  function trackPromotion(name, slot, whenSent) {
    return sendEvent(
      name,
      {
        promotion_id: CODE,
        promotion_name: bar.getAttribute('data-sale-name') || CODE,
        creative_slot: slot,
      },
      whenSent
    );
  }

  /* ── copy ───────────────────────────────────────────────────────────── */
  function confirmCopy() {
    if (!copied) return;
    copied.classList.add('is-on');
    setTimeout(function () {
      copied.classList.remove('is-on');
    }, 1400);
  }

  function copyFallback() {
    var ta = document.createElement('textarea');
    ta.value = CODE;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
    } catch (e) {
      /* clipboard unavailable - the code is on screen anyway */
    }
    document.body.removeChild(ta);
    confirmCopy();
  }

  if (chip) {
    chip.addEventListener('click', function () {
      // Tracked separately from Apply: copy-vs-apply says whether shoppers
      // trust the one-tap flow or would rather paste it in at checkout.
      trackPromotion('select_promotion', 'announcement_bar_copy');

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(CODE).then(confirmCopy, copyFallback);
      } else {
        copyFallback();
      }
    });
  }

  /* ── lazy Lottie ────────────────────────────────────────────────────── */
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        reject(new Error('failed to load ' + src));
      };
      document.head.appendChild(s);
    });
  }

  function loadDiscountAnim() {
    if (discountAssets) return discountAssets;

    discountAssets = Promise.all([
      window.lottie ? Promise.resolve() : loadScript(playerUrl),
      window.ZoeyDiscount ? Promise.resolve() : loadScript(discountUrl),
    ]).then(function () {
      if (!window.lottie || !window.ZoeyDiscount) throw new Error('discount animation unavailable');
      return window.ZoeyDiscount;
    });

    return discountAssets;
  }

  // The badge ships in LottieFiles' own teal-and-blue palette, which belongs to
  // nothing on this store. Every fill and stroke in it is a flat colour — no
  // gradients anywhere — so the artwork can be kept and the palette swapped
  // wholesale on the way in. Keys are the authored colours, values ours.
  var DISCOUNT_PALETTE = {
    '00c99f': '664228', // badge disc     -> brand brown
    '4e6aff': 'b5822a', // ribbon         -> gold
    '8fa2ff': '8a5f1e', // ribbon fold    -> deeper gold, so the fold still reads
    e7efff: 'f2e4cd', //   halo           -> cream
    ffa100: 'f2c94c', //   sparkle        -> the candle yellow off the cake
    ff4d69: 'c2694a', //   sparkle        -> terracotta
    // white is left alone: it is the "25%" itself and the highlights, and it
    // reads on brown exactly as it read on teal.
  };

  function hexOf(rgba) {
    if (!Array.isArray(rgba) || rgba.length < 3) return null;
    var out = '';
    for (var i = 0; i < 3; i++) {
      if (typeof rgba[i] !== 'number') return null;
      var v = Math.round(rgba[i] * 255).toString(16);
      out += v.length < 2 ? '0' + v : v;
    }
    return out;
  }

  function recolour(rgba) {
    var hex = hexOf(rgba);
    if (!hex) return;
    var swap = DISCOUNT_PALETTE[hex];
    if (!swap) return;
    for (var i = 0; i < 3; i++) {
      rgba[i] = parseInt(swap.substr(i * 2, 2), 16) / 255;
    }
  }

  // Colour lives in two shapes in Lottie: a static `k` of [r,g,b,a], or an
  // animated `k` of keyframes each holding their own `s` array. Both occur here.
  function repaint(node) {
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) repaint(node[i]);
      return;
    }
    if (!node || typeof node !== 'object') return;

    if ((node.ty === 'fl' || node.ty === 'st') && node.c && node.c.k) {
      var k = node.c.k;
      if (typeof k[0] === 'number') recolour(k);
      else {
        for (var j = 0; j < k.length; j++) {
          if (k[j] && Array.isArray(k[j].s)) recolour(k[j].s);
        }
      }
    }

    for (var key in node) {
      if (Object.prototype.hasOwnProperty.call(node, key)) repaint(node[key]);
    }
  }

  // The animation is a counter: a text layer stepping through ~250 keyframes
  // from 0% up to whatever it was authored at. Rather than bake our number into
  // the asset, the keyframes are rescaled on the way in, so the animation always
  // counts to the discount the theme setting actually says.
  //
  // The peak is read out of the data rather than assumed, so replacing the
  // source animation with one that counts to some other figure still works.
  //
  // Recolouring rides along on the same clone: two passes over 92KB is wasteful
  // when one copy serves both.
  function rescaleCounter(data, percent) {
    var clone;
    try {
      clone = JSON.parse(JSON.stringify(data));
    } catch (e) {
      return data;
    }

    repaint(clone.layers);

    if (!(percent > 0)) return clone;

    var layers = clone.layers || [];
    for (var i = 0; i < layers.length; i++) {
      var layer = layers[i];
      if (layer.ty !== 5 || !layer.t || !layer.t.d || !layer.t.d.k) continue;

      var keys = layer.t.d.k;
      var peak = 0;
      var j;

      for (j = 0; j < keys.length; j++) {
        var found = parseInt(String(keys[j].s && keys[j].s.t).replace(/[^0-9]/g, ''), 10);
        if (!isNaN(found) && found > peak) peak = found;
      }
      if (!peak) continue;

      for (j = 0; j < keys.length; j++) {
        var value = parseInt(String(keys[j].s && keys[j].s.t).replace(/[^0-9]/g, ''), 10);
        if (isNaN(value)) continue;
        keys[j].s.t = Math.round((value * percent) / peak) + '%';
      }
    }

    return clone;
  }

  // Reduced motion gets the number without the count-up — the figure is the
  // information, the animation is only how it arrives.
  function playDiscount(mount, fallback) {
    if (!mount || reduce.matches) return Promise.resolve();

    return loadDiscountAnim().then(function (data) {
      window.lottie.loadAnimation({
        container: mount,
        renderer: 'canvas',
        loop: false,
        autoplay: true,
        animationData: rescaleCounter(data, discountPercent),
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet',
          clearCanvas: true,
        },
      });

      // Only now is the static number redundant. Hiding it earlier would leave
      // an empty box for however long the player takes to arrive, or forever if
      // it never does.
      mount.setAttribute('data-playing', 'true');
      if (fallback) fallback.hidden = true;
    });
  }

  /* ── welcome dialog ─────────────────────────────────────────────────────
     An automatic discount is invisible until checkout. Without a word, a
     shopper reading "25% off" goes looking for a code that does not exist, and
     the ones who fail to find it conclude the offer is not really on. This says
     it once, plainly, and then never again.

     Deliberately not on the first paint: it waits for load and then a beat, so
     it never competes with LCP and never lands on someone mid-scroll. */
  (function welcome() {
    var dialog = document.querySelector('[data-zoey-welcome]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    // Not sessionStorage: "once" has to mean once for the run of the sale, not
    // once per tab. Keyed by code so a future sale gets its own turn.
    var key = dialog.getAttribute('data-once-key');

    // ?zoey-welcome=1 reopens it and leaves no trace, so the thing can be
    // looked at twenty times in a row while it is being built. Harmless in
    // production: it does nothing unless someone types it, and it only ever
    // shows a panel the shopper could already have seen.
    var forced = /[?&]zoey-welcome=1(&|$)/.test(window.location.search);

    function seen() {
      if (forced) return false;
      try {
        return window.localStorage.getItem(key) === '1';
      } catch (e) {
        // Private mode has no memory, and a dialog that reappears every visit
        // is worse than one that never shows.
        return true;
      }
    }

    function markSeen() {
      // A forced open is a look, not a visit — remembering it would defeat the
      // point of being able to force it.
      if (forced) return;
      try {
        window.localStorage.setItem(key, '1');
      } catch (e) {
        /* nothing to remember with */
      }
    }

    // The bar hides itself when a creator's code owns the session — greeting
    // that visitor with our sale would step on the creator's commission just as
    // loudly as the bar would.
    if (bar.hidden || seen()) return;

    /* ── how it ended ─────────────────────────────────────────────────────
       Whether people like this is not answered by counting OK presses: OK is
       the only obvious way out, so almost everyone who does anything at all
       presses it. The distinction that carries meaning is *how* they left and
       *how long* they stayed.

         ok_button  — read it and accepted
         backdrop   — clicked past it
         escape     — dismissed it outright
         abandoned  — left the page with it still open, the worst outcome

       Reported once per open, whichever happens first, so the four are shares
       of the same denominator. Seconds open separates a considered read from a
       reflex swat: a wave of one-second ok_button presses is people getting rid
       of it, not people pleased by it, and the two look identical in a bare
       click count. */
    var openedAt = 0;
    var reported = false;

    function reportOutcome(method, useBeacon) {
      if (reported) return;
      reported = true;

      var params = {
        promotion_id: CODE,
        promotion_name: bar.getAttribute('data-sale-name') || CODE,
        creative_slot: 'birthday_welcome_dialog',
        method: method,
        seconds_open: openedAt ? Math.round((Date.now() - openedAt) / 100) / 10 : 0,
      };

      // A page being torn down will not wait for an XHR. Beacon survives it;
      // without this the abandon case — the one worth knowing about — is
      // precisely the one that never reports.
      if (useBeacon) params.transport_type = 'beacon';

      sendEvent('zoey_bday_dismiss', params);
    }

    // Escape fires `cancel` before `close`, so the reason is known by the time
    // the close handler runs.
    dialog.addEventListener('cancel', function () {
      reportOutcome('escape');
    });

    // showModal does not dismiss on a backdrop click by default, and on a phone
    // there is no Escape key — so without this the OK button is the only way
    // out, which is exactly the coercion that makes people resent a popup.
    // The dialog element itself is the backdrop; anything inside is the panel.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        reportOutcome('backdrop');
        dialog.close();
      }
    });

    // Closing the tab or navigating away with it still open. pagehide covers
    // the back/forward cache, which visibilitychange alone misses on iOS.
    window.addEventListener('pagehide', function () {
      if (dialog.open) reportOutcome('abandoned', true);
    });

    var closeBtn = dialog.querySelector('[data-zoey-welcome-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        // The only event this sends. A separate zoey_bday_ack_click used to go
        // out alongside it, which described exactly the same people as
        // method=ok_button — pressing OK is a dismissal, just the good kind.
        // Two names for one population is a thing to keep in step forever, so
        // the duplicate is gone and method carries the distinction.
        reportOutcome('ok_button');

        // Nothing waits on the event: there is no navigation here, so the hit
        // has the rest of the pageview to leave rather than a reload racing it.
        dialog.close();
      });
    }

    // Written the moment it opens, not on close. Someone who dismisses it by
    // navigating away has still seen it, and showing it again would read as a
    // dialog that will not take no for an answer.
    dialog.addEventListener('close', markSeen);

    function open() {
      if (dialog.open) return;
      markSeen();
      openedAt = Date.now();
      dialog.showModal();
      trackPromotion('view_promotion', 'birthday_welcome_dialog');
      playDiscount(
        dialog.querySelector('[data-zoey-discount-anim]'),
        dialog.querySelector('[data-zoey-discount-fallback]')
      ).catch(function () {
        /* the static number is already on screen */
      });
    }

    function schedule() {
      setTimeout(open, 900);
    }

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  })();

  /* ── countdown ──────────────────────────────────────────────────────────
     Counts to an absolute instant supplied by Liquid as a Unix timestamp, so a
     shopper in Vancouver sees the same deadline as one in Delhi without us
     guessing at an offset.

     The DAYS cell is dropped once there are none left rather than sitting at
     00 — a dead column steals attention from the ones still moving. */
  // Takes the clock rather than finding it, so the bar and the welcome dialog
  // can each run their own against the same deadline. Two instances, one
  // source of truth: both read the timestamp Liquid rendered.
  function countdown(clock) {
    if (!clock) return;

    var cells = clock.querySelector('[data-zoey-clock-cells]');
    var endsAt = parseInt(clock.getAttribute('data-ends'), 10) * 1000;
    if (!cells || !endsAt) return;

    var timer = null;
    var digits = null; // built once per column set, then only their text changes
    var built = '';

    function pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    // Built with real nodes rather than a markup string: this repaints every
    // second, and swapping innerHTML would rebuild seven elements a tick for
    // the sake of four characters.
    function build(labels) {
      cells.textContent = '';
      digits = {};

      labels.forEach(function (label, i) {
        if (i) {
          var sep = document.createElement('span');
          sep.className = 'zoey-bar__sep';
          sep.textContent = ':';
          cells.appendChild(sep);
        }

        var cell = document.createElement('span');
        cell.className = 'zoey-bar__cell';

        var value = document.createElement('b');
        var name = document.createElement('span');
        name.textContent = label;

        cell.appendChild(value);
        cell.appendChild(name);
        cells.appendChild(cell);

        digits[label] = value;
      });

      built = labels.join();
    }

    function paint() {
      var left = endsAt - Date.now();

      if (left <= 0) {
        // Someone left the page open across the deadline. The section stops
        // rendering server-side on the next load; until then, say nothing
        // rather than sit at 00:00:00 advertising a sale that has closed.
        clock.hidden = true;
        stop();
        return;
      }

      var d = Math.floor(left / 86400000);
      var labels = d > 0 ? ['days', 'hrs', 'min', 'sec'] : ['hrs', 'min', 'sec'];

      if (built !== labels.join()) build(labels);

      if (digits.days) digits.days.textContent = pad(d);
      digits.hrs.textContent = pad(Math.floor(left / 3600000) % 24);
      digits.min.textContent = pad(Math.floor(left / 60000) % 60);
      digits.sec.textContent = pad(Math.floor(left / 1000) % 60);

      clock.hidden = false;
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      if (timer) return;
      paint();
      timer = setInterval(paint, 1000);
    }

    // No reason to tick in a background tab — browsers throttle it anyway, and
    // a stale clock on return is worse than one that repaints on focus.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    start();
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-zoey-clock]'), countdown);

  /* ── the bar was seen ───────────────────────────────────────────────── */
  // Only counts as a promotion view when there is actually an offer on screen.
  // The applied state isn't an offer, and a bar hidden behind a creator's code
  // was never shown at all — counting either would flatter the click-through
  // rate with impressions that could never convert.
  // An auto-applied bar is still an offer on screen — the saving is real and
  // the shopper is seeing it announced. It just needs no click, so it reports
  // impressions with no select_promotion to follow. Without this exception the
  // sale would run its whole length with no promotion views recorded at all.
  if (!bar.hidden && (bar.getAttribute('data-state') === 'offer' || bar.getAttribute('data-auto') === 'true')) {
    trackPromotion('view_promotion', 'announcement_bar');
  }

  /* ── apply ──────────────────────────────────────────────────────────── */
  function apply() {
    if (applyBtn) applyBtn.setAttribute('aria-busy', 'true');

    var redirect = encodeURIComponent(window.location.pathname + window.location.search);
    var route = '/discount/' + encodeURIComponent(CODE) + '?redirect=' + redirect;

    return fetch(route, { credentials: 'same-origin' })
      .then(function (response) {
        // fetch only rejects on network failure, so a 401/404/500 arrives here
        // looking like success. `shopify theme dev` returns 401 for this route,
        // which would otherwise show "applied" over a discount that never took.
        if (!response.ok) throw new Error('discount route returned ' + response.status);

        // Hold the reload until the hit is away — see trackPromotion.
        trackPromotion('select_promotion', 'announcement_bar_apply', function () {
          window.location.reload();
        });
      })
      .catch(function () {
        // Fall back to the plain navigation the Collabs links use.
        window.location.href = route;
      });
  }

  // The markup ships a plain link to /discount/CODE, which works on its own.
  // Intercept it only once we know we can do better.
  if (applyBtn) {
    applyBtn.addEventListener('click', function (event) {
      event.preventDefault();
      apply();
    });
  }
})();
